"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { SWRConfig } from "swr";

import { MantineProvider } from "@mantine/core";
import { useIsomorphicEffect } from "@mantine/hooks";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast as toastOrg, ToastContainer } from "react-toastify";
import { usePathname } from "next/navigation";

import { MobileWrapper } from "@/template/MobileWrapper";
import { handleAPIError, handleAPIRequest } from "@/utils/handleAPIError";

export function Providers({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  const queryClient = useMemo(() => new QueryClient(), []);

  useEffect(() => {
    toastOrg.dismiss();
  }, [pathname]);

  useIsomorphicEffect(() => {
    handleAPIError();
    handleAPIRequest();

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isWebView = /(wv)|webview/.test(userAgent);

    function openApp() {
      if (!isWebView) {
        if (isIOS || isAndroid) {
          window.location.href = "exp+disco://";
        }
      }
    }

    openApp();
  }, []);

  return (
    <SWRConfig
      value={{
        suspense: false,
        revalidateOnFocus: false,
        dedupingInterval: 10000,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <MantineProvider>
          <MobileWrapper>{children}</MobileWrapper>
        </MantineProvider>
      </QueryClientProvider>

      <ToastContainer
        position="bottom-center"
        icon={false}
        hideProgressBar
        autoClose={3000}
        pauseOnFocusLoss={false}
        closeOnClick={false}
        limit={3}
        className="w-full fixed bottom-[50px] z-[10000] box-border p-4"
        toastClassName={
          "rounded-12 shadow-popup text-white-85 relative mb-8 box-border flex cursor-pointer items-center justify-center"
        }
        bodyClassName="my-auto mx-0 flex flex-1 items-center [&_div]:flex [&_div]:items-center [&_div]:w-full"
      />
    </SWRConfig>
  );
}
